import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Icon } from '../../assets/icons/Icon';
import styles from './Header.module.scss';
import clsx from 'clsx';
import { Stepper } from '@mantine/core';

interface HeaderProps {
  isOnboarding?: boolean;
  currentStep?: number;
  showStepper?: boolean;
  onStepClick?: (step: number) => void;
  user: {
    name: string;
    email?: string;
    jobTitle?: string | null;
    avatarUrl?: string | null;
  };
  logout: () => void;
  isLeader?: boolean;
  isSponsor?: boolean;
  hideNav?: boolean;
}

const Header = ({ isOnboarding, currentStep, showStepper, onStepClick, user, logout, isLeader, isSponsor, hideNav }: HeaderProps) => {
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };
  const userName = user.name;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowPopover(false);
      }
    }
    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopover]);

  return (
    <header className={styles.header} role="banner">
      <div className={styles.header__container}>
        <span style={{ flexShrink: 0, width: 131, height: 24 }}>
          <Icon iconName="Logo" width={131} height={24} />
        </span>
        {showStepper && isOnboarding && (
          <div className={styles.stepperContainer}>
            <Stepper
              active={currentStep ? currentStep - 1 : 0}
              size="sm"
              color="blue"
              onStepClick={onStepClick ? (step) => onStepClick(step + 1) : undefined}
              completedIcon={null}
              styles={{
                separator: {
                  backgroundColor: 'var(--color-gray-3)',
                  height: 2,
                  minWidth: 62,
                },
              }}
            >
              <Stepper.Step 
                label="Behaviour selection" 
              
                allowStepClick={!!onStepClick}
                completedIcon={null}
              />
              <Stepper.Step 
                label="Habit selection" 
                allowStepClick={!!onStepClick}
                completedIcon={null}
              />
            </Stepper>
          </div>
        )}
        {!isOnboarding && !hideNav && (
          <>
            <button
              className={styles.menuToggle}
              aria-label="Open menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span />
              <span />
              <span />
            </button>
            <nav className={`${styles.header__nav} ${menuOpen ? styles.open : ''}`} role="navigation" aria-label="Main navigation">
              <div className={styles.header__tabs}>
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `${styles.header__tab} ${isActive ? styles['header__tab--active'] : ''}`
                  }
                  end
                  onClick={() => setMenuOpen(false)}
                >
                  <span className={styles.header__tabText}>Dashboard</span>
                </NavLink>
                <NavLink
                  to="/leaderboard"
                  className={({ isActive }) =>
                    `${styles.header__tab} ${isActive ? styles['header__tab--active'] : ''}`
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  <span className={styles.header__tabText}>Insights</span>
                </NavLink>
                <NavLink
                  to="/about"
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `${styles.header__tab} ${isActive ? styles['header__tab--active'] : ''}`
                  }
                >
                  <span className={styles.header__tabText}>About</span>
                </NavLink>
                {(isLeader || isSponsor) && (
                  <NavLink
                    to="/summary"
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `${styles.header__tab} ${isActive ? styles['header__tab--active'] : ''}`
                    }
                  >
                    <span className={styles.header__tabText}>Goals & KPIs</span>
                  </NavLink>
                )}
              </div>
            </nav>
          </>
        )}
            <div
          className={clsx(styles.header__profile, { [styles['header__profile--active']]: showPopover })}
              onClick={() => setShowPopover((v) => !v)}
              tabIndex={0}
              style={{ cursor: 'pointer', position: 'relative' }}
              ref={popoverRef}
            >
              <div className={styles.header__avatar} aria-label={`${userName} profile`}>
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={userName}
                    className={styles.header__avatarImg}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove(styles.hidden);
                    }}
                  />
                ) : null}
                <div className={`${styles.header__avatarContainer}${user.avatarUrl ? ` ${styles.hidden}` : ''}`}>
                  <span className={styles.header__avatarText}>{getInitials(userName)}</span>
                </div>
              </div>
          <span className={styles.header__userName}>{userName}</span>
          <Icon 
            iconName="ArrowDownIcon" 
            width="0.63rem" 
            height="0.38rem" 
            className={clsx(styles.header__arrowIcon, { [styles['header__arrowIcon--open']]: showPopover })}
          />
              {showPopover && (
                <div className={styles.header__popover} onClick={(e) => e.stopPropagation()}>
              <div 
                className={styles.header__popoverUserRow}
                onClick={() => {
                  navigate('/profile?tab=profile');
                  setShowPopover(false);
                }}
              >
                <div className={styles.header__popoverAvatar}>
                  {user.avatarUrl ? (
                    <img src={user.avatarUrl} alt={userName} className={styles.header__popoverAvatarImg} />
                  ) : (
                    <span className={styles.header__avatarText}>{getInitials(userName)}</span>
                  )}
                </div>
                <div className={styles.header__popoverUserInfo}>
                  <span className={styles.header__popoverUserName}>{userName}</span>
                  {user.email && <span className={styles.header__popoverUserEmail}>{user.email}</span>}
                </div>
                <Icon iconName="ArrowRightIcon" width="0.33rem" height="0.58rem" />
              </div>
              <div className={styles.header__popoverDivider} />
              <button className={styles.header__popoverItem} onClick={() => { navigate('/profile?tab=history'); setShowPopover(false); }}>
                <Icon iconName="ClockIcon" width="1.25rem" height="1.25rem" />
                Habit tracking history
              </button>
              <button className={styles.header__popoverItem} onClick={logout}>
                <Icon iconName="LogoutIcon" width="1.25rem" height="1.25rem" />
                Logout
              </button>
            </div>
        )}
        </div>
      </div>

    </header>
  );
};

export default Header;
